import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnalysisAPI, OdkAPI } from "../api/endpoints";
import ProjectFormPicker from "../components/ProjectFormPicker";
import { Card, PageHeader, Spinner, ErrorBanner, HelpBox, KindBadge } from "../components/Layout";
import PlotlyChart from "../components/Plot";
import { varKind } from "../lib/varkind";
import type { Data } from "plotly.js-dist-min";

interface BootstrapResult {
  error?: string;
  method?: string;
  overall_mean?: number;
  std_error?: number;
  ci_lower?: number;
  ci_upper?: number;
  ci_percentile?: { lower: number; upper: number };
  ci_bca?: { lower: number; upper: number };
  means_distribution?: number[];
}

export default function BootstrapPage() {
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [target, setTarget] = useState("");
  const [dummyVal, setDummyVal] = useState("");
  const [n, setN] = useState(1000);
  const [size, setSize] = useState(100);
  const [method, setMethod] = useState("bca");
  const [result, setResult] = useState<BootstrapResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const colsQ = useQuery({
    queryKey: ["dict", projectId, formId],
    queryFn: () => OdkAPI.variables(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });
  const cols = (colsQ.data ?? []).filter((v) => !v.is_technical);
  const targetEntry = cols.find((c) => c.name === target);
  const targetIsCategorical = targetEntry ? varKind(targetEntry) !== "numeric" : false;

  // Categorías reales en los datos (para estimar la proporción de una categoría).
  const catsQ = useQuery({
    queryKey: ["freq", projectId, formId, target],
    queryFn: async () => {
      const r = await AnalysisAPI.descriptives({
        project_id: projectId!, form_id: formId!, variables: [target],
      });
      const entry = (r as Record<string, { counts?: Record<string, number> }>)[target];
      return Object.keys(entry?.counts ?? {});
    },
    enabled: !!projectId && !!formId && !!target && targetIsCategorical,
  });
  const categories = catsQ.data ?? [];

  const canRun = !!target && (!targetIsCategorical || !!dummyVal);

  const run = async () => {
    if (!projectId || !formId || !canRun) return;
    setBusy(true); setErr(null);
    try {
      const r = await AnalysisAPI.bootstrap({
        project_id: projectId, form_id: formId, target_var: target,
        n_samples: n, sample_size: size, method,
        dummy_val: targetIsCategorical && dummyVal ? dummyVal : undefined,
      });
      setResult(r as BootstrapResult);
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
    finally { setBusy(false); }
  };

  const histTrace: Data = {
    type: "histogram",
    x: result?.means_distribution ?? [],
    marker: { color: "#0ea5e9" },
  };

  const isProportion = targetIsCategorical && !!dummyVal;
  const fmt = (v?: number) =>
    v == null ? "—" : isProportion ? `${(v * 100).toFixed(2)}%` : v.toFixed(4);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader title="Bootstrap" subtitle="Intervalos de confianza por remuestreo, sin asumir normalidad." />

      <HelpBox title="Guía: qué es el bootstrap y cómo usar cada opción">
        <p>
          El <b>bootstrap</b> estima qué tan precisa es una media (o proporción) remuestreando los propios
          datos: toma muchas muestras al azar con reemplazo, calcula la media de cada una y construye la
          distribución de esas medias. De ahí salen el error estándar y el intervalo de confianza, sin
          suponer que los datos siguen una distribución normal.
        </p>
        <div>
          <b>Opciones:</b>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li><b>Variable.</b> Si es <b>numérica</b> se estima su media. Si es <b>categórica</b>, elige la categoría de interés: se estima la <b>proporción</b> de esa categoría (ej.: % que respondió "Sí").</li>
            <li><b>Muestras.</b> Cuántas re-muestras se generan (1000 es un buen estándar; más = resultado más estable pero más lento).</li>
            <li><b>Tamaño.</b> Cuántos casos se sortean en cada re-muestra. Debe ser ≤ que el número de respuestas válidas de la variable.</li>
            <li><b>IC Percentil.</b> Toma los percentiles 2.5% y 97.5% de la distribución de medias. Simple y directo.</li>
            <li><b>IC BCa.</b> Percentil corregido por sesgo y asimetría (bias-corrected and accelerated). Recomendado: más preciso cuando la distribución no es simétrica.</li>
          </ul>
        </div>
        <p>
          <b>Cómo leer el resultado.</b> El histograma muestra la distribución de las medias re-muestreadas;
          si es angosto, tu estimación es precisa. El intervalo [inferior, superior] contiene el valor real
          con 95% de confianza.
        </p>
      </HelpBox>

      <Card className="mb-4">
        <ProjectFormPicker projectId={projectId} formId={formId}
          onProject={(id) => { setProjectId(id); setFormId(null); }} onForm={setFormId} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <div>
            <label className="etiqueta">Variable</label>
            <select className="input" value={target}
              onChange={(e) => { setTarget(e.target.value); setDummyVal(""); }}>
              <option value="">—</option>
              {cols.map((c) => (
                <option key={c.name} value={c.name}>
                  {(c.label || c.name) + (varKind(c) === "numeric" ? "" : " (categórica)")}
                </option>
              ))}
            </select>
            {targetEntry && <div className="mt-1"><KindBadge kind={varKind(targetEntry)} /></div>}
          </div>
          <div>
            <label className="etiqueta" title="Cuántas re-muestras se generan">Muestras</label>
            <input type="number" min={100} max={10000} value={n} onChange={(e) => setN(Number(e.target.value))} className="input" />
          </div>
          <div>
            <label className="etiqueta" title="Casos sorteados en cada re-muestra">Tamaño</label>
            <input type="number" min={10} value={size} onChange={(e) => setSize(Number(e.target.value))} className="input" />
          </div>
          <div>
            <label className="etiqueta">Método del IC</label>
            <select className="input" value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="bca">BCa (recomendado)</option><option value="percentile">Percentil</option>
            </select>
          </div>
        </div>

        {targetIsCategorical && target && (
          <div className="mt-3 p-3 rounded-lg bg-estado-warning/10 border border-estado-warning/40">
            <div className="text-sm text-estado-serio font-medium mb-1">Variable categórica: estimar una proporción</div>
            <p className="text-xs text-tinta-2 mb-2">
              "{targetEntry?.label || target}" no es numérica, así que no tiene media. Elige la categoría
              de interés y el bootstrap estimará el <b>% de esa categoría</b> con su intervalo de confianza.
            </p>
            <select className="input sm:w-80"
              value={dummyVal} onChange={(e) => setDummyVal(e.target.value)}>
              <option value="">— elegir categoría —</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {catsQ.isLoading && <div className="mt-1 text-xs text-tinta-3">Cargando categorías reales de los datos…</div>}
          </div>
        )}

        <button onClick={run} disabled={busy || !canRun}
          className="btn mt-3">
          {busy ? <Spinner /> : "Ejecutar"}
        </button>
        {targetIsCategorical && !dummyVal && target && (
          <span className="ml-3 text-xs text-estado-serio">Elige la categoría a estimar para poder ejecutar.</span>
        )}
      </Card>

      {err && <ErrorBanner message={err} />}
      {result?.error && <ErrorBanner message={result.error} />}

      {result && !result.error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <div className="text-sm space-y-1">
              {isProportion && (
                <div className="text-xs text-tinta-3 mb-1">
                  Estimando la proporción de "{dummyVal}" en "{targetEntry?.label || target}".
                </div>
              )}
              <div>{isProportion ? "Proporción" : "Media"}: <span className="text-marca">{fmt(result.overall_mean)}</span></div>
              <div>Error estándar: {fmt(result.std_error)}</div>
              <div>
                IC 95% ({result.method === "bca" ? "BCa" : "percentil"}): [{fmt(result.ci_lower)}, {fmt(result.ci_upper)}]
              </div>
              <div className="text-xs text-tinta-3 mt-2">
                Percentil: [{fmt(result.ci_percentile?.lower)}, {fmt(result.ci_percentile?.upper)}]<br />
                BCa: [{fmt(result.ci_bca?.lower)}, {fmt(result.ci_bca?.upper)}]
              </div>
            </div>
          </Card>
          <Card>
            <div className="text-sm font-medium text-tinta-2 mb-2">Distribución de medias re-muestreadas</div>
            <div className="h-72"><PlotlyChart data={[histTrace]} layout={{ xaxis: { title: isProportion ? "Proporción muestral" : "Media muestral" } }} /></div>
          </Card>
        </div>
      )}
    </div>
  );
}
